import { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useProjectOrganizationId } from 'store/project';
import { useCompanies } from 'store/company';
import { useSites } from 'store/site';
import { populateFinances } from 'shared/domain/company/mapping/toModel';
import { companyModelToCompanyOnView } from 'views/companies/mapping';
import { CompanyDataGrid } from '../CompanyDataGrid';
import { CompanyDataGridRow } from '../CompanyDataGrid/columns';
import { useFinancialDataInitializer, useFinancialDataStore } from 'store/financialData';

export function CompanyTable(): ReactElement {
  const { items: companies, emptyListReason, loading } = useCompanies();

  useFinancialDataInitializer();
  const { financialData, loading: financialLoading, receivedAt } = useFinancialDataStore.use.data();

  const {
    items: sites,
  } = useSites();

  const intl = useIntl();
  const organizationId = useProjectOrganizationId();

  const companiesOnView: CompanyDataGridRow[] = useMemo(
    () =>
      companies.map((company) => {
        // First, populate financial data
        const companyWithFinances = {
          ...company,
          ...populateFinances(company._id, financialData, financialLoading),
          customId: `${company._id}_${receivedAt}`,
        };
        
        // Then transform to CompanyOnView (this will properly convert sites from IDs to LabelledEntity[])
        const companyOnView = companyModelToCompanyOnView(
          company,
          sites,
          intl,
          organizationId
        );
        
        return {
          ...companyOnView,
          financialData: companyWithFinances.financialData,
          correspondenceAddress: company.correspondenceAddress,
          registeredOfficeAddress: company.registeredOfficeAddress,
        };
      }),
    [companies, sites, financialData, financialLoading, receivedAt, intl, organizationId]
  );

  const isLoading = loading || financialLoading;

  return (
    <CompanyDataGrid
      data={companiesOnView}
      loading={isLoading}
      emptyListReason={isLoading ? undefined : emptyListReason}
    />
  );
}
